/**
 * Stream SSE de finanzas del Event Hub (`GET /event/finance`).
 * Contrato: SSE_FINANZAS_FRONTEND.md. Cada evento es solo una señal: el dato
 * completo se vuelve a pedir por HTTP (invalidación de tags en RTK Query).
 */
import { PARTNER_INVOICE_STATUS_LABELS, PAYOUT_STATUS_LABELS } from '../constants/partnerInvoice';

export type FinanceEntity = 'partner-invoice' | 'partner-payout' | 'payout';

export const FINANCE_ENTITIES: FinanceEntity[] = ['partner-invoice', 'partner-payout', 'payout'];

export const FINANCE_EVENTS = [
  'partner-invoice.created',
  'partner-invoice.updated',
  'partner-payout.created',
  'partner-payout.updated',
  'payout.created',
  'payout.updated',
] as const;

export type FinanceEventName = (typeof FINANCE_EVENTS)[number];

export interface FinanceEventData {
  id: string;
  partnerId: string;
  /** Estado DESPUÉS del cambio. Enum numérico. */
  status: number;
  /** Correlativo legible: INV-… (factura), PAY-… / PPAY-… (pagos). */
  number: string;
  updatedAt: string;
}

export interface FinanceEvent {
  name: FinanceEventName;
  entity: FinanceEntity;
  data: FinanceEventData;
  /** false si el `lastEventId` ya se había visto (duplicado: refetch sí, toast no). */
  isNew: boolean;
}

// PartnerPayout (partner → Sama). FAILED (4) no es final: admite reintento y vuelve a 2.
const PARTNER_PAYOUT_STATUS_LABELS: Record<number, string> = {
  1: 'Pendiente',
  2: 'En proceso',
  3: 'Confirmado',
  4: 'Fallido',
  5: 'Cancelado',
  6: 'Verificando',
};

const STATUS_LABELS: Record<FinanceEntity, Record<number, string>> = {
  'partner-invoice': PARTNER_INVOICE_STATUS_LABELS,
  'partner-payout': PARTNER_PAYOUT_STATUS_LABELS,
  payout: PAYOUT_STATUS_LABELS,
};

export const financeStatusLabel = (entity: FinanceEntity, status: number) =>
  STATUS_LABELS[entity][status] ?? 'Estado desconocido';

export interface FinanceStreamOptions {
  getToken: () => string | null | undefined;
  /** Solo superadmin: filtra a un partner. El partner nunca lo manda (403 si es ajeno). */
  partnerId?: string;
  onEvent: (event: FinanceEvent) => void;
  /** Reconexión exitosa: lo ocurrido mientras estaba caído no se reenvía. */
  onResync: () => void;
  /** El ticket respondió 401: el JWT ya no sirve. */
  onAuthLost: () => void;
}

const WATCHDOG_MS = 60_000;
const MAX_BACKOFF_MS = 30_000;
const SEEN_LIMIT = 200;

export function openFinanceStream(opts: FinanceStreamOptions): () => void {
  const apiUrl = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3000' : '');
  const hubUrl = import.meta.env.VITE_SSE_URL || (import.meta.env.DEV ? 'http://localhost:3001' : apiUrl);

  let es: EventSource | null = null;
  let stopped = false;
  let attempt = 0;
  let opened = false;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  const seen = new Set<string>();

  const kick = () => {
    attempt = 0;
    clearTimeout(watchdog);
    watchdog = setTimeout(reconnect, WATCHDOG_MS);
  };

  const remember = (id: string) => {
    if (seen.has(id)) return false;
    seen.add(id);
    if (seen.size > SEEN_LIMIT) seen.delete(seen.values().next().value!);
    return true;
  };

  // Un ticket por conexión: vive 20 s y el hub lo consume al conectar.
  async function fetchTicket(): Promise<string | null> {
    const token = opts.getToken();
    if (!token) return null;
    const res = await fetch(`${apiUrl}/sse/notification/ticket`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) return null;
    if (!res.ok) throw new Error(`ticket ${res.status}`);
    const body = (await res.json()) as { ticket?: string };
    if (!body.ticket) throw new Error('ticket vacío');
    return body.ticket;
  }

  async function connect() {
    if (stopped) return;
    let ticket: string | null;
    try {
      ticket = await fetchTicket();
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[FinanceSSE] ticket falló', e);
      return scheduleRetry();
    }
    if (stopped) return;
    if (!ticket) {
      stopped = true;
      opts.onAuthLost();
      return;
    }

    const params = new URLSearchParams({ ticket });
    if (opts.partnerId) params.set('partnerId', opts.partnerId);

    const source = new EventSource(`${hubUrl}/event/finance?${params}`);
    es = source;

    source.onopen = () => {
      kick();
      // La primera apertura no resincroniza: las vistas recién montadas ya pidieron sus datos.
      if (opened) opts.onResync();
      opened = true;
    };

    source.addEventListener('ping', kick);

    for (const name of FINANCE_EVENTS) {
      source.addEventListener(name, (e: MessageEvent<string>) => {
        kick();
        let data: FinanceEventData;
        try {
          data = JSON.parse(e.data) as FinanceEventData;
        } catch {
          return;
        }
        const isNew = !e.lastEventId || remember(e.lastEventId);
        opts.onEvent({ name, entity: name.split('.')[0] as FinanceEntity, data, isNew });
      });
    }

    // El reintento nativo reusaría el ticket ya consumido (401 seguro): cerramos y pedimos otro.
    source.onerror = reconnect;
  }

  function reconnect() {
    es?.close();
    es = null;
    clearTimeout(watchdog);
    scheduleRetry();
  }

  function scheduleRetry() {
    if (stopped) return;
    const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt++);
    retryTimer = setTimeout(connect, base / 2 + Math.random() * (base / 2));
  }

  void connect();

  return () => {
    stopped = true;
    clearTimeout(retryTimer);
    clearTimeout(watchdog);
    es?.close();
    es = null;
  };
}
